import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Box, Button, Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PersonaDetalle } from "../schema/person.schema";
import { BreadcrumbComponent } from "@/components/breadcrumb/Breadcrumb";
import { AppRoutes } from "@/constants/routes";
import { TabComponent } from "@/components/tabs/TabComponent";
import { PersonMainDataCard } from "@/components/common/section-components/card/PersonMainDataCard";
import { AssociatedAccounts } from "../tabs/AssociatedAccounts";
import { PersonalData } from "../tabs/PersonalData";
import { FamilyData } from "../tabs/FamilyData";
import { AppColors } from "@/styles/colors";
import { PenLine } from "lucide-react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { PersonBeneficiaryAgenda } from "../tabs/PersonBeneficiaryAgenda";
import { fetchPersonDetailHandler } from "../schema/handler-functions";

export const PersonDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isActive, setIsActive] = useState<boolean>(false);

  const [loadingPersonData, setLoadingPersonData] = useState<boolean>(false);
  const [personData, setPersonData] = useState<PersonaDetalle | null>();

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchPersonDetail = async (id: number) => {
    await fetchPersonDetailHandler({
      id,
      setLoadingPersonData,
      setPersonData,
      navigate,
      fetchPermissions,
      routeOnPermissionError: AppRoutes.PERSON_DETAIL.replace(":id", String(id)),
    });
  };

  useEffect(() => {
    if (isActive && id) fetchPersonDetail(Number(id));
  }, [isActive, id]);

  const location = useLocation();
  const handleBack = () => {
    navigate(AppRoutes.PERSONS, {
      state: {
        page: location?.state?.page ?? 1,
      },
    });
  };

  const handleEditPersonData = () => {
    navigate(AppRoutes.PERSON_UPDATE.replace(":id", String(id)));
  };

  const [tabValue, setTabValue] = useState<string | null>("datos_personales");

  const { state } = useLocation();

  useEffect(() => {
    if (state?.tab && typeof state?.tab === "string") {
      setTabValue(state?.tab);
    }
  }, [state]);

  const { userPermissions, fetchPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.PERSONS &&
        p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={false}
        backButton
        onBack={handleBack}
        title={`Maestro de Personas`}
        loading={loadingPersonData}
        breadcrumbComponent={
          personData?.id && (
            <BreadcrumbComponent
              options={[
                {
                  label: "Maestro de personas",
                  href: AppRoutes.PERSONS,
                },
                {
                  label: personData?.nombre + " " + personData?.apellido,
                  href: "#",
                  strong: true,
                },
              ]}
            />
          )
        }
      />

      <Flex flexDir="column" gap="15px">
        {/* Main data card */}
        <PersonMainDataCard personData={personData} />
        <Box height="1px" backgroundColor={AppColors.WHITE_BORDER_1} />
        {personData && (
          <TabComponent
            value={tabValue}
            onValueChange={(e) => setTabValue(e.value)}
            tabs={[
              {
                label: "Datos personales",
                content: (
                  <>
                    {hasWriteAccess && (
                      <Flex justifyContent="end">
                        <Button
                          justifySelf="end"
                          paddingX="8px"
                          marginY="10px"
                          bgColor={AppColors.GREEN_PRIMARY}
                          onClick={handleEditPersonData}
                        >
                          <PenLine />
                          Editar
                        </Button>
                      </Flex>
                    )}

                    <PersonalData personData={personData} />
                  </>
                ),
                value: "datos_personales",
              },
              {
                label: "Cuentas asociadas",
                content: (
                  <AssociatedAccounts
                    personData={personData}
                    fetchPersonDetail={fetchPersonDetail}
                  />
                ),
                value: "cuentas_asociadas",
              },
              {
                label: "Datos familiares",
                content: <FamilyData personData={personData} />,
                value: "datos_familiares",
              },
              {
                label: "Destinatarios",
                content: (
                  <PersonBeneficiaryAgenda
                    personData={personData}
                    fetchPersonDetail={fetchPersonDetail}
                  />
                ),
                value: "destinatarios",
              },
            ]}
          />
        )}
      </Flex>
    </Flex>
  );
};
